import { useMemo, useRef, useState, type ChangeEventHandler } from 'react';
import { createPortal } from 'react-dom';
import { Paperclip } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useResultRequiredModalStore } from '@/entities/task/model/stores/result-required-modal';
import { DocumentIcon } from '@/components/icons/documentIcon';
import { BackIcon } from '@/components/icons/backIcon';
import { BookmarkIcon } from '@/components/icons/bookmarkIcon';
import { toast } from 'sonner';
import { saveResultRequired } from '@/entities/task/api/saveResultRequired';

function FileList({ files }: { files: { id: string; name: string }[] }) {
  if (files.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-[5px] min-w-0">
      {files.map((f) => (
        <button
          key={f.id}
          type="button"
          title={f.name}
          className="w-fit max-w-full flex items-center gap-[6.64px] text-left group"
        >
          <DocumentIcon color="#000000" />
          <span className="text-[14px] leading-[130%] tracking-[-0.83px] font-normal text-black underline underline-offset-2 truncate">
            {f.name}
          </span>
        </button>
      ))}
    </div>
  );
}

export function ResultRequiredModal() {
  const isOpen = useResultRequiredModalStore((s) => s.isOpen);
  const payload = useResultRequiredModalStore((s) => s.payload);

  const resultText = useResultRequiredModalStore((s) => s.resultText);
  const files = useResultRequiredModalStore((s) => s.files);

  const close = useResultRequiredModalStore((s) => s.close);
  const clearForm = useResultRequiredModalStore((s) => s.clearForm);

  const setResultText = useResultRequiredModalStore((s) => s.setResultText);
  const addFiles = useResultRequiredModalStore((s) => s.addFiles);
  const runAfterSaveAction = useResultRequiredModalStore(
    (s) => s.runAfterSaveAction
  );

  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [saving, setSaving] = useState(false);

  const canSave = useMemo(() => {
    return resultText.trim().length > 0 || files.length > 0;
  }, [resultText, files.length]);

  if (!isOpen) {
    return null;
  }

  const pickFiles = () => fileInputRef.current?.click();

  const onFilesPicked: ChangeEventHandler<HTMLInputElement> = (e) => {
    const picked = Array.from(e.target.files ?? []);
    if (!picked.length) {
      return;
    }

    addFiles(picked);
    e.target.value = '';
  };

  async function handleSave() {
    if (!canSave || saving) {
      return;
    }

    // taskId нужен для инвалидации до clearForm (он чистит payload).
    const taskId = payload?.taskId != null ? String(payload.taskId) : null;

    setSaving(true);
    try {
      // Сохраняем результат (текст + файлы → /chat/results, с файлами также
      // попадает в чат задачи)
      await saveResultRequired();

      // Обновляем таб «Результаты», карточку задачи и чат
      if (taskId) {
        queryClient.invalidateQueries({ queryKey: ['task-results', taskId] });
        queryClient.invalidateQueries({ queryKey: ['task', taskId] });
        queryClient.invalidateQueries({ queryKey: ['task-chat', taskId] });
      }

      toast.success('Результат сохранён');

      // После сохранения — открыть флоу завершения
      runAfterSaveAction();

      // Закрыть и почистить форму
      close();
      clearForm();
    } catch (e: ANY) {
      console.error('ResultRequiredModal save error', e);
      toast.error(
        e?.message ? String(e.message) : 'Не удалось сохранить результат'
      );
    } finally {
      setSaving(false);
    }
  }

  function handleBackToTask() {
    close();
  }

  return createPortal(
    <div className="fixed inset-0 z-[9999] font-roboto">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-md animate-in fade-in-0 duration-300"
        onClick={close}
        style={{ cursor: 'pointer' }}
      />

      <div className="absolute inset-0 grid place-items-center px-4">
        <div
          className="w-full max-w-[450px] lg:max-w-[1010px] rounded-[20px] overflow-hidden bg-white animate-in fade-in-0 zoom-in-95 duration-300 ease-out"
          style={{
            boxShadow:
              '0px 0px 11px rgba(51, 193, 231, 0.4), 0px 0px 13px rgba(51, 193, 231, 0.4)',
            backdropFilter: 'blur(2px)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-[20px] py-[31px] lg:px-[24px] lg:py-[40px] flex flex-col gap-[12px] lg:gap-[38px]">
            <p className="text-[#14222C] text-[16px] lg:text-[20px] leading-[130%] tracking-[-0.5px] font-normal">
              В задаче не указан результат. Укажите его в текстовом поле и
              нажмите на «Сохранить».
            </p>

            <div className="flex flex-col lg:flex-row items-stretch gap-[12px] lg:gap-[32px]">
              {/* Поле результата + вложения */}
              <div className="flex-1 min-w-0 rounded-[16.98px] bg-[#F8F7FC] shadow-[0_3.54px_12.88px_rgba(0,0,0,0.03)] px-[11px] py-[14px] min-h-[90px] lg:min-h-[112px] flex flex-col gap-[11px]">
                <textarea
                  placeholder="Введите результат или подкрепите файл (обязательно)"
                  value={resultText}
                  onChange={(e) => setResultText(e.target.value)}
                  disabled={saving}
                  className="w-full flex-1 min-h-[36px] lg:min-h-[60px] bg-transparent border-0 outline-none resize-none text-[#14222C] text-[14px] leading-[130%] tracking-[-0.5px] placeholder:text-black/60"
                />

                <div className="flex items-end justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <FileList files={files} />
                  </div>

                  <button
                    type="button"
                    onClick={pickFiles}
                    disabled={saving}
                    className="shrink-0 text-black/60 hover:text-black transition"
                    title="Прикрепить файл"
                    aria-label="Прикрепить файл"
                  >
                    <Paperclip size={18} />
                  </button>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={onFilesPicked}
                  disabled={saving}
                />
              </div>

              {/* Кнопки */}
              <div className="flex flex-row lg:flex-col gap-[16px] lg:gap-[24px] lg:w-[188px] shrink-0">
                <button
                  type="button"
                  onClick={handleBackToTask}
                  disabled={saving}
                  className="
                    flex-1 lg:flex-none
                    inline-flex items-center justify-center gap-[10px] leading-none
                    h-[44px] px-[18px]
                    rounded-[24px]
                    bg-white text-black
                    text-[16px] tracking-[-0.83px] font-normal
                    shadow-[0_3.54px_12.88px_rgba(0,0,0,0.03)] ring-1 ring-black/10
                    transition hover:bg-black/[0.02]
                    disabled:opacity-50 disabled:cursor-not-allowed
                  "
                >
                  <BackIcon
                    width={24}
                    height={24}
                    className="shrink-0 [&_path]:fill-black"
                  />
                  Вернуться в задачу
                </button>

                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!canSave || saving}
                  className="
                    flex-1 lg:flex-none
                    inline-flex items-center justify-center gap-[10px] leading-none
                    h-[44px] px-[18px]
                    rounded-[24px]
                    bg-[#57B792] text-white
                    text-[16px] tracking-[-0.83px] font-normal
                    transition hover:brightness-105
                    disabled:opacity-50 disabled:cursor-not-allowed
                  "
                >
                  <BookmarkIcon width={24} height={24} className="shrink-0" />
                  {saving ? 'Сохраняю…' : 'Сохранить'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
